/**
 * FERP Portal - Setting List Page (DRR-125 Part 12)
 *
 * Config Type #4 (st-): Setting bundles — define the operational context for
 * a tenant: industry vertical, subject label, active lenses, regulatory
 * frameworks, and navigation group structure. There is only ONE active setting
 * per tenant at a time, returned by the settingBundle() query.
 *
 * Architecture:
 *   - Backend: settingBundle() query in scope_api/scope_graphql.rs
 *   - Config source: common/configs/seed/settings/healthcare/st-0-*
 *   - DRR-103 Phase 3: Used by ScopeBreadcrumbNavigator for scope labels
 *   - DRR-075: Flat SK — CFG#SETTING#st-0-0
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend: all data from backend settingBundle query
 * - No hardcoded field values — displays what backend returns
 * - Admin view layer only
 */

import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  Accordion,
  Loader,
  SimpleGrid,
  Paper,
} from '@mantine/core';
import {
  IconSettings,
  IconAlertTriangle,
  IconInfoCircle,
  IconShieldCheck,
  IconEye,
  IconMap,
  IconBuilding,
  IconUser,
  IconNetwork,
} from '@tabler/icons-react';
import {
  useGetSettingBundleAdminQuery,
  type SettingBundle,
  type SettingNavigationGroup,
} from '@/generated/graphql';

export function SettingListPage() {
  const { data, loading, error } = useGetSettingBundleAdminQuery();

  const setting = data?.settingBundle;

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Setting Bundle">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Active Setting</Title>
            <Text c="dimmed" size="sm">
              Config type st- — operational context for this tenant
            </Text>
          </div>
          {setting && (
            <Badge variant="light" color="blue" size="lg" ff="monospace">
              {setting.settingId}
            </Badge>
          )}
        </Group>

        {/* Info banner */}
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="blue"
          title="About Settings"
        >
          Settings define the operational context for this tenant. The active setting determines
          which regulations, care vocabulary, and navigation groups apply. Only one setting is
          active at a time — switching settings requires a tenant configuration change.
        </Alert>

        {loading && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        {setting && (
          <>
            {/* Identity card */}
            <Card withBorder p="lg">
              <Group gap="sm" mb="md">
                <ThemeIcon variant="light" color="blue" size="lg">
                  <IconSettings size={20} />
                </ThemeIcon>
                <div>
                  <Text fw={700} size="lg">{setting.name}</Text>
                  <Text c="dimmed" size="sm">{setting.industryLabel}</Text>
                </div>
              </Group>

              <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
                <Paper withBorder p="sm">
                  <Group gap="xs" mb={4}>
                    <IconBuilding size={14} />
                    <Text size="xs" c="dimmed" fw={500}>Code System</Text>
                  </Group>
                  <Text size="sm" fw={500}>{setting.codeSystem}</Text>
                  <Text size="xs" c="dimmed" ff="monospace">{setting.code}</Text>
                </Paper>

                <Paper withBorder p="sm">
                  <Group gap="xs" mb={4}>
                    <IconNetwork size={14} />
                    <Text size="xs" c="dimmed" fw={500}>Vertical</Text>
                  </Group>
                  <Text size="sm" fw={500}>{setting.vertical}</Text>
                </Paper>

                <Paper withBorder p="sm">
                  <Group gap="xs" mb={4}>
                    <IconUser size={14} />
                    <Text size="xs" c="dimmed" fw={500}>Subject Label</Text>
                  </Group>
                  <Text size="sm" fw={500}>{setting.subjectLabel}</Text>
                </Paper>

                <Paper withBorder p="sm">
                  <Group gap="xs" mb={4}>
                    <IconEye size={14} />
                    <Text size="xs" c="dimmed" fw={500}>Active Lenses</Text>
                  </Group>
                  <Text size="sm" fw={500}>{setting.activeLenses.length}</Text>
                  <Text size="xs" c="dimmed">
                    {setting.activeLenses.join(', ')}
                  </Text>
                </Paper>
              </SimpleGrid>
            </Card>

            {/* Regulatory frameworks */}
            <Card withBorder p="md">
              <Group gap="sm" mb="sm">
                <ThemeIcon variant="light" color="red" size="sm">
                  <IconShieldCheck size={14} />
                </ThemeIcon>
                <Text fw={600}>Regulatory Frameworks</Text>
              </Group>
              <Group gap="xs">
                {setting.regulatoryFrameworks.map((framework) => (
                  <Badge key={framework} variant="light" color="red" size="sm">
                    {framework}
                  </Badge>
                ))}
              </Group>
            </Card>

            {/* Active lenses detail */}
            {setting.activeLenses.length > 0 && (
              <Card withBorder p="md">
                <Group gap="sm" mb="sm">
                  <ThemeIcon variant="light" color="violet" size="sm">
                    <IconEye size={14} />
                  </ThemeIcon>
                  <Text fw={600}>Active Lenses</Text>
                </Group>
                <Group gap="xs">
                  {setting.activeLenses.map((lens) => (
                    <Badge key={lens} variant="outline" color="violet" size="sm" ff="monospace">
                      {lens}
                    </Badge>
                  ))}
                </Group>
              </Card>
            )}

            {/* Navigation groups accordion */}
            {setting.navigationGroups.length > 0 && (
              <Card withBorder p="md">
                <Group gap="sm" mb="md">
                  <ThemeIcon variant="light" color="teal" size="sm">
                    <IconMap size={14} />
                  </ThemeIcon>
                  <Text fw={600}>Navigation Groups ({setting.navigationGroups.length})</Text>
                </Group>

                <Accordion variant="separated">
                  {[...setting.navigationGroups]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((group: SettingNavigationGroup) => (
                      <Accordion.Item key={group.id} value={group.id}>
                        <Accordion.Control>
                          <Group gap="sm">
                            <Badge variant="light" color="teal" size="xs">
                              {group.layer}
                            </Badge>
                            <Text size="sm" fw={500}>{group.label}</Text>
                            <Text size="xs" c="dimmed" ff="monospace">{group.id}</Text>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap="xs">
                            <Group gap="xl">
                              <div>
                                <Text size="xs" c="dimmed">Sort Order</Text>
                                <Text size="sm">{group.sortOrder}</Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed">Icon</Text>
                                <Text size="sm" ff="monospace">{group.icon}</Text>
                              </div>
                            </Group>
                            <div>
                              <Text size="xs" c="dimmed" mb={4}>Member Patterns</Text>
                              <Group gap="xs">
                                {group.memberPatterns.map((pattern) => (
                                  <Badge
                                    key={pattern}
                                    variant="outline"
                                    size="xs"
                                    color="gray"
                                    ff="monospace"
                                  >
                                    {pattern}
                                  </Badge>
                                ))}
                              </Group>
                            </div>
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    ))}
                </Accordion>
              </Card>
            )}

            {/* Definition raw JSON (collapsed by default) */}
            <Card withBorder p="md">
              <Accordion variant="default">
                <Accordion.Item value="definition">
                  <Accordion.Control>
                    <Text size="sm" fw={500}>Full Definition (JSON)</Text>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text size="xs" ff="monospace" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {setting.definition}
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Card>
          </>
        )}
      </Stack>
    </Container>
  );
}

export default SettingListPage;
